import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useAuditLog } from '@/hooks/useAuditLog';
import { supabase } from '@/integrations/supabase/client';
import {
  FileText,
  Calendar,
  TrendingUp,
  Users,
  AlertTriangle,
  Download,
  Copy,
  Sparkles,
  Clock,
  CheckCircle2,
  Loader2,
  History,
  ClipboardList,
  Brain,
  MessageSquare,
} from 'lucide-react';
import AuditLogPanel from './AuditLogPanel';
import { buildProjectDataSnapshot } from '@/lib/reportSnapshot';
import { downloadReportPdf } from '@/lib/reportPdf';
import { ReportContent } from './ReportContent';
import { AIExplainability, type AIExplanation } from '@/components/ai/AIExplainability';

interface ReportType {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  frequency: string;
  color: string;
}

import { AIOutputStatusBadge, AIOutputStatusWorkflow, type AIOutputState } from './AIOutputStatus';
import { useLocalization } from '@/hooks/useLocalization';

interface GeneratedReport {
  id: string;
  type: string;
  name: string;
  content: string;
  generatedAt: string;
  status: AIOutputState;
  explanation?: AIExplanation;
}

const reportTypes: ReportType[] = [
  {
    id: 'weekly-status',
    name: 'Weekly Status Report',
    description: 'Comprehensive overview of weekly progress, accomplishments, and upcoming tasks',
    icon: Calendar,
    frequency: 'Weekly',
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  },
  {
    id: 'monthly-summary',
    name: 'Monthly Summary',
    description: 'Detailed monthly analysis with KPIs, budget status, and strategic insights',
    icon: TrendingUp,
    frequency: 'Monthly',
    color: 'bg-primary/10 text-primary border-primary/20',
  },
  {
    id: 'stakeholder-update',
    name: 'Stakeholder Update',
    description: 'Executive-level summary for stakeholders with key decisions and project health',
    icon: Users,
    frequency: 'Bi-weekly',
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  },
  {
    id: 'risk-assessment',
    name: 'Risk Assessment',
    description: 'Comprehensive risk analysis with mitigation strategies and trend analysis',
    icon: AlertTriangle,
    frequency: 'Weekly',
    color: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  },
  {
    id: 'team-performance',
    name: 'Team Performance',
    description: 'Team productivity metrics, AI efficiency gains, and collaboration insights',
    icon: Users,
    frequency: 'Weekly',
    color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  },
];

const ReportsView = () => {
  const { tx, t } = useLocalization();
  const { toast } = useToast();
  const { logStatusChange } = useAuditLog();
  const [activeTab, setActiveTab] = useState('generate');
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<GeneratedReport | null>(null);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);

  // Load persisted reports from ai_outputs
  const loadReports = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('ai_outputs')
      .select('id, output_type, title, payload, status, created_at')
      .eq('source_module', 'generate-report')
      .eq('generated_by', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error || !data) return;

    const loaded: GeneratedReport[] = data.map((row) => ({
      id: row.id,
      type: row.output_type,
      name: row.title,
      content: (row.payload as Record<string, unknown>)?.content as string ?? '',
      generatedAt: row.created_at,
      status: (['draft', 'reviewed', 'approved', 'published'] as AIOutputState[]).includes(row.status as AIOutputState)
        ? (row.status as AIOutputState)
        : 'draft',
    }));

    setGeneratedReports(loaded);
    if (loaded.length > 0) setSelectedReport(loaded[0]);
  }, []);

  // Load on mount, and again when the chat assistant creates a report
  useEffect(() => {
    void loadReports();
    const onGenerated = () => {
      void loadReports();
      setActiveTab('view');
    };
    window.addEventListener('masira:report-generated', onGenerated);
    return () => window.removeEventListener('masira:report-generated', onGenerated);
  }, [loadReports]);

  const generateExplanation = async (report: GeneratedReport) => {
    setIsLoadingExplanation(true);
    try {
      const projectData = await buildProjectDataSnapshot();
      const { data, error } = await supabase.functions.invoke('generate-ai-explanation', {
        body: {
          type: 'report',
          context: {
            reportType: report.type,
            projectData,
          },
          outputContent: report.content,
        },
      });

      if (error) throw error;

      const updatedReport = { ...report, explanation: data };
      setSelectedReport(updatedReport);
      setGeneratedReports(prev =>
        prev.map(r => (r.id === report.id ? updatedReport : r))
      );

      toast({
        title: t('Explanation Generated'),
        description: t('AI explainability details are now available.'),
      });
    } catch (error) {
      console.error('Error generating explanation:', error);
      toast({
        title: t('Explanation Failed'),
        description: t('Could not generate AI explanation.'),
        variant: "destructive",
      });
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  const handleGenerateReport = async (reportType: ReportType) => {
    setIsGenerating(reportType.id);

    try {
      const projectData = await buildProjectDataSnapshot();
      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: {
          reportType: reportType.id,
          projectData
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      const newReport: GeneratedReport = {
        id: data.id ?? Date.now().toString(),
        type: reportType.id,
        name: reportType.name,
        content: data.report,
        generatedAt: data.generatedAt,
        status: 'draft',
      };

      setGeneratedReports(prev => [newReport, ...prev]);
      setSelectedReport(newReport);
      setActiveTab('view');

      toast({
        title: t('Report Generated'),
        description: t('{0} has been generated successfully.', { 0: reportType.name }),
      });

      // Auto-generate explanation
      setTimeout(() => generateExplanation(newReport), 500);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: t('Generation Failed'),
        description: error instanceof Error ? error.message : t('Failed to generate report. Please try again.'),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(null);
    }
  };

  const handleCopyReport = async () => {
    if (!selectedReport) return;
    
    try {
      await navigator.clipboard.writeText(selectedReport.content);
      toast({
        title: t('Copied'),
        description: t('Report content copied to clipboard.'),
      });
    } catch {
      toast({
        title: t('Copy Failed'),
        description: t('Failed to copy report content.'),
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = () => {
    if (!selectedReport) return;

    downloadReportPdf(
      {
        title: selectedReport.name,
        generatedAt: selectedReport.generatedAt,
        content: selectedReport.content,
        status: selectedReport.status,
      },
      `${selectedReport.type}-${new Date().toISOString().split('T')[0]}.pdf`,
    );

    toast({
      title: t('Downloaded'),
      description: t('Report has been downloaded as PDF.'),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('Auto-Generated Reports')}</h1>
          <p className="text-muted-foreground">{t('AI-powered reports from your project data')}</p>
        </div>
        <Badge variant="secondary" className="gap-1">
          <Sparkles className="h-3 w-3" />
          
          {t('Powered by AI')}
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generate">{t('Generate Report')}</TabsTrigger>
          <TabsTrigger value="view" disabled={!selectedReport}>
            
            {t('View Report')} {selectedReport && <CheckCircle2 className="h-3 w-3 ms-1" />}
          </TabsTrigger>
          <TabsTrigger value="history">
            
            {t('History (')}{generatedReports.length})
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />
            
            {t('Audit Log')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const isLoading = isGenerating === report.id;

              return (
                <Card key={report.id} className={`hover:shadow-md transition-all ${report.color} border-2`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-background flex items-center justify-center">
                        <Icon className="h-6 w-6" />
                      </div>
                      <Badge variant="outline">{report.frequency}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{report.name}</CardTitle>
                    <CardDescription>{tx(report.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full gap-2"
                      onClick={() => window.dispatchEvent(new CustomEvent('masira:start-report-flow', { detail: { templateId: report.id } }))}
                      disabled={isGenerating !== null}
                    >
                      <MessageSquare className="h-4 w-4" />
                      
                      {t('Set up with Ask Masira')}
                    </Button>
                    <Button
                      variant="ghost"
                      className="mt-2 w-full gap-2"
                      onClick={() => handleGenerateReport(report)}
                      disabled={isLoading || isGenerating !== null}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          
                          {t('Generating...')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4" />
                          
                          {t('Quick generate')}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* AI Info Card */}
          <Card className="mt-6 bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{t('How AI Reports Work')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    
                    {t('Reports are automatically generated by analyzing your project data including tasks, meetings, emails, and activity metrics. AI synthesizes this information into professional, actionable reports that would typically take hours to create manually.')}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-2 text-emerald-500">
                      <Clock className="h-4 w-4" />
                      <span>{t('Saves 2-4 hours per report')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-primary">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{t('Consistent formatting')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-500">
                      <FileText className="h-4 w-4" />
                      <span>{t('Export to PDF')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="view" className="mt-6">
          {selectedReport && (
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {selectedReport.name}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3" />
                        
                        {t('Generated')} {new Date(selectedReport.generatedAt).toLocaleString()}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleCopyReport}>
                        <Copy className="h-4 w-4 me-1" />
                        
                        {t('Copy')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleDownloadReport}>
                        <Download className="h-4 w-4 me-1" />
                        
                        {t('Download')}
                      </Button>
                    </div>
                  </div>
                  {/* AI Output Status Workflow */}
                  <div className="border-t border-border pt-4">
                    <AIOutputStatusWorkflow 
                      status={selectedReport.status} 
                      onStatusChange={async (newStatus) => {
                        const previousStatus = selectedReport.status;

                        // Persist first — the workflow badge and History tab
                        // both read status back from ai_outputs on reload, so
                        // without this write every transition reverts to
                        // "draft" on refresh (only the audit log entry below
                        // would survive, orphaned from the actual state).
                        const { error: updateError } = await supabase
                          .from('ai_outputs')
                          .update({ status: newStatus })
                          .eq('id', selectedReport.id);

                        if (updateError) {
                          console.error('Failed to persist report status:', updateError.message);
                          toast({
                            title: t('Status Update Failed'),
                            description: t('Could not save the new status. Please try again.'),
                            variant: "destructive",
                          });
                          return;
                        }

                        const updatedReport = { ...selectedReport, status: newStatus };
                        setSelectedReport(updatedReport);
                        setGeneratedReports(prev =>
                          prev.map(r => r.id === selectedReport.id ? updatedReport : r)
                        );

                        // Log to audit
                        await logStatusChange({
                          reportType: selectedReport.type,
                          reportName: selectedReport.name,
                          previousStatus,
                          newStatus,
                        });

                        toast({
                          title: t('Status Updated'),
                          description: t('Report status changed to {0}.', { 0: newStatus }),
                        });
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* AI Explainability Panel */}
                {selectedReport.explanation ? (
                  <AIExplainability explanation={selectedReport.explanation} />
                ) : (
                  <Card className="border-dashed border-primary/30 bg-primary/5">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Brain className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-medium">{t('AI Explainability')}</p>
                            <p className="text-xs text-muted-foreground">
                              
                              {t('Understand how this report was generated')}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateExplanation(selectedReport)}
                          disabled={isLoadingExplanation}
                        >
                          {isLoadingExplanation ? (
                            <>
                              <Loader2 className="h-3 w-3 me-1 animate-spin" />
                              
                              {t('Loading...')}
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3 me-1" />
                              
                              {t('Generate')}
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <ScrollArea className="h-[480px] pe-4">
                  <ReportContent content={selectedReport.content} className="rounded-lg border border-border bg-card p-6" />
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {generatedReports.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">{t('No Reports Yet')}</h3>
                <p className="text-muted-foreground mb-4">
                  
                  {t('Generate your first report to see it here')}
                </p>
                <Button onClick={() => setActiveTab('generate')}>
                  
                  {t('Generate Report')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {generatedReports.map((report) => (
                <Card 
                  key={report.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedReport(report);
                    setActiveTab('view');
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{report.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(report.generatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <AIOutputStatusBadge status={report.status} size="sm" />
                        <Button variant="ghost" size="sm">
                          
                          {t('View')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <AuditLogPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsView;
